import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AssignedLocationDto, PlaceTypeEnum } from '@buspro/web/apis';

@Component({
  selector: 'buspro-admin-assigned-locations-card',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="card bg-white shadow-md rounded-lg">
      <div class="card-body p-6">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold">My Assigned Locations</h3>
          <span class="badge badge-primary">{{ locations.length }}</span>
        </div>
        <div class="flex flex-wrap gap-3">
          @for (location of locations; track location.id) {
            <button
              (click)="onLocationClick(location.id)"
              class="location-badge px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer"
              [class.bg-blue-200]="selectedLocationId === location.id"
            >
              <div class="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-5 h-5 text-blue-600"
                >
                  @switch (getLocationTypeIcon(location.type)) {
                    @case ('terminal') {
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"
                      />
                    }
                    @case ('bus-stop') {
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                      />
                    }
                    @default {
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                      />
                    }
                  }
                </svg>
                <div class="text-left">
                  <p class="font-medium text-gray-900">{{ location.name }}</p>
                  @if (location.address) {
                    <p class="text-xs text-gray-500">{{ location.address }}</p>
                  }
                </div>
              </div>
            </button>
          } @empty {
            <div class="w-full text-center py-4">
              <p class="text-sm text-gray-500">No locations assigned</p>
            </div>
          }
        </div>
      </div>
    </div>
  `,
})
export class AssignedLocationsCardComponent {
  @Input() locations: AssignedLocationDto[] = [];
  @Input() selectedLocationId: string | null = null;
  @Output() locationSelected = new EventEmitter<string>();

  onLocationClick(locationId: string): void {
    this.locationSelected.emit(locationId);
  }

  getLocationTypeIcon(type: PlaceTypeEnum | 'STANDARD_PLACE' | string): string {
    const typeStr = String(type);
    if (typeStr === PlaceTypeEnum.Terminal || typeStr === PlaceTypeEnum.TerminalBusStop || typeStr === 'TERMINAL') {
      return 'terminal';
    }
    if (typeStr === PlaceTypeEnum.BusStop || typeStr === 'BUS_STOP') {
      return 'bus-stop';
    }
    return 'standard-place';
  }
}
